import { useRef } from 'react'
import { twMerge } from 'tailwind-merge'

import { Line } from '../../models/Line'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import { ImageView } from '../common/ImageView'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextField } from '../common/fields/TextField'
import { RemovableItemContainer } from '../workflows/RemovableItemContainer'
import { getIconFromLineType } from './NewChargeReceiptView'

type Props = {
    line: Line
    max: number
    isEditable: boolean
    onQuickEdit: (line: Line) => void
    onEdit: () => void
    onRemove: () => void
}

export function NewChargeLineView({
    line,
    max,
    isEditable,
    onQuickEdit,
    onEdit,
    onRemove,
}: Props) {
    const name = line.item ? line.item.name : line.itemDisplay

    const ref = useRef<HTMLInputElement>(null)

    return (
        <RemovableItemContainer onRemove={onRemove} deleteButtonMargin="-right-13">
            <div
                className={twMerge(
                    'cursor-pointer hover:bg-white hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-gray-700',
                    'active:bg-linear-to-b',
                    'disabled:border-gray-200',
                    'rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 active:from-black/4 active:to-black/4'
                )}
                onClick={(e: React.MouseEvent<any>) => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (!isEditable) {
                        return
                    }

                    if (e.target === ref.current || ref.current?.contains(e.target as Node)) {
                        return
                    }

                    onEdit()
                }}
            >
                <div className="flex justify-start gap-4">
                    <ImageView
                        image={line.item?.image}
                        fallbackIcon={{
                            icon: `${getIconFromLineType(line)}.small`,
                            background: 'round',
                            className: 'size-9 shrink-0 rounded-full border-2 border-white',
                        }}
                        className="size-9 shrink-0 rounded-full border-2 border-white"
                    />
                    <Label size="sm" className="self-center text-left">
                        {name}
                    </Label>
                </div>

                <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2" ref={ref}>
                        <Label size="sm">Qty</Label>
                        <TextField
                            value={line.quantity}
                            disabled={!isEditable}
                            onChange={(e) => {
                                if (Number.isNaN(Number(e.target.value))) {
                                    return
                                }
                                const qty = Number(e.target.value)
                                const rate = line.rate ?? 0
                                const newLine = line.clone({
                                    quantity: qty,
                                    total: rate ? rate * qty : undefined,
                                })
                                onQuickEdit(newLine)
                            }}
                            min={0}
                            className="max-w-16"
                        />
                        <CurrencyField
                            key={line.rate}
                            value={line.rate}
                            disabled={!isEditable}
                            onChange={(rate) => {
                                const qty = line.quantity ?? 0
                                const newLine = line.clone({
                                    rate: (rate ?? 0).toFixed(2),
                                    total: rate ? rate * qty : undefined,
                                })
                                onQuickEdit(newLine)
                            }}
                            max={max}
                            className="max-w-32 shrink text-right"
                        />
                    </div>
                    {isEditable && (
                        <Button intent="tertiaryLink" size="small" className="p-0" onClick={onEdit}>
                            Edit
                        </Button>
                    )}
                </div>
            </div>
        </RemovableItemContainer>
    )
}
